import React, { useState, useEffect } from 'react';
import { getSMTPConfig, updateSMTPConfig, testSMTPConnection } from '../services/api';
import { TextField, Button, Box, Alert, Typography, useTheme } from '@mui/material';

const SMTPConfig = () => {
  const theme = useTheme();
  const [config, setConfig] = useState({
    host: '',
    port: 465,
    username: '',
    password: '',
    from_email: '',
  });
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState({ type: '', content: '' });
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  useEffect(() => {
    fetchSMTPConfig();
  }, []);

  const fetchSMTPConfig = async () => {
    setLoading(true);
    try {
      const response = await getSMTPConfig();
      setConfig(response.data);
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', content: '获取SMTP配置失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
        const { name, value } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: name === 'port' ? parseInt(value, 10) : value
        }));
    };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateSMTPConfig(config);
      setMessage({ type: 'success', content: 'SMTP配置更新成功' });
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', content: '更新SMTP配置失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', content: '请输入测试邮箱地址' });
      return;
    }
    setTestingConnection(true);
    try {
      await testSMTPConnection({ config, testEmail });
      setMessage({ type: 'success', content: 'SMTP测试邮件发送成功' });
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', content: `SMTP连接测试失败: ${error.message}` });
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 500,
        mx: 'auto',
        mt: 5,
        p: 3,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
        SMTP 配置
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="SMTP 主机"
          name="host"
          value={config.host}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="SMTP 端口"
          name="port"
          type="number"
          value={config.port}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="用户名"
          name="username"
          value={config.username}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="密码"
          name="password"
          type="password"
          value={config.password}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="发件人邮箱"
          name="from_email"
          value={config.from_email}
          onChange={handleChange}
          margin="normal"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="测试邮箱地址"
          name="testEmail"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          margin="normal"
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              },
              '&:hover fieldset': {
                borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[400],
              },
            },
          }}
        />
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? '正在更新...' : '更新配置'}
          </Button>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleTestConnection}
            disabled={testingConnection}
            sx={{
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
              },
            }}
          >
            {testingConnection ? '测试中...' : '测试连接'}
          </Button>
        </Box>
      </form>

      {message.content && (
        <Alert 
          severity={message.type === 'error' ? 'error' : 'success'} 
          sx={{ 
            mt: 3,
            backgroundColor: theme.palette.mode === 'dark' 
              ? (message.type === 'error' ? theme.palette.error.dark : theme.palette.success.dark)
              : (message.type === 'error' ? theme.palette.error.light : theme.palette.success.light),
            color: theme.palette.getContrastText(
              theme.palette.mode === 'dark'
                ? (message.type === 'error' ? theme.palette.error.dark : theme.palette.success.dark)
                : (message.type === 'error' ? theme.palette.error.light : theme.palette.success.light)
            ),
          }}
        >
          {message.content}
        </Alert>
      )}
    </Box>
  );
};

export default SMTPConfig;